import { FC, useEffect, useState } from 'react';
import Select, { createFilter } from 'react-select';
import styled from 'styled-components';
import { Theme } from '../theme';
import { DOWN_KEY, ENTER_KEY, ESCAPE_KEY, SPACE_KEY, TAB_KEY } from '../../utils/keys';
import useTheme from '../../utils/useTheme';
import { Icon } from '../Icon/Icon';
import { IconWrapper, ISelectIcon } from '../Select/Select';
import MenuList from '../Select/SelectMenuList';
import { sortOptions } from '../../utils/sorting';
import { RadioList } from '../RadioList/RadioList';
import { RadioListItem } from '../RadioList/RadioListItem';
import { Label } from '../Label/Label';
import { SvgIcon } from '../SvgIcons/svgIcons';

export const SelectMiniDropdown = styled.div`
  display: inline-block;
  position: relative;
`;

export const SelectMiniButton = styled.button`
  appearance: none;
  background: none;
  border-radius: 0;
  border: none;
  box-shadow: none;
  color: ${props => props.theme.vars.newColor.blue2};
  cursor: pointer;
  display: flex;
  font-family: inherit;
  font-weight: 600;
  justify-content: space-between;
  margin: 0;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  padding: 0;
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'initial')};
  &&:hover {
    color: ${props => props.theme.vars.newColor.blue3};
  }
  &&:disabled {
    color: ${props => props.theme.vars.newColor.darkGS3};
  }
`;

export const SelectMiniDropdownIndicator = styled.div`
  margin-left: 5px;
`;

export const SelectMiniMenu = styled.div<Pick<ISelectMiniProps, 'menuPlacement' | 'menuWidth' | 'radioButtonMenu'>>`
  background-color: ${props => props.theme.vars.newColor.white};
  border-radius: none;
  bottom: ${({ menuPlacement }) => (menuPlacement === 'bottom' || menuPlacement === 'bottomRight' ? '20px' : 'auto')};
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1), 0 4px 11px rgba(0, 0, 0, 0.1);
  box-sizing: border-box;
  margin-bottom: 8px;
  margin-top: 8px;
  position: absolute;
  right: ${({ menuPlacement }) => (menuPlacement === 'right' || menuPlacement === 'bottomRight' ? 0 : 'auto')};
  width: ${({ menuWidth, radioButtonMenu }) => (menuWidth ? menuWidth : radioButtonMenu ? 'auto' : '200px')};
  z-index: 2;
`;
export const SelectMiniBlanket = styled.div`
  bottom: 0;
  left: 0;
  top: 0;
  right: 0;
  position: fixed;
  z-index: 1;
`;
export const SelectMiniContainer = styled.div`
  display: flex;
`;

export const SelectMiniStyles = (theme: Theme) => ({
  container: () => ({
    boxSizing: 'border-box',
    position: 'relative',
    zIndex: 2,
  }),
  control: (styles, { isFocused, selectProps: { isSearchable } }) => {
    if (!isSearchable) {
      return {
        background: 0,
        border: 0,
        color: 'transparent',
        fontSize: 'inherit',
        left: '0',
        opacity: 0,
        outline: 0,
        padding: 0,
        position: 'absolute',
        transform: 'scale(0)',
        width: '1px',
      };
    }

    return {
      alignItems: 'center',
      backgroundColor: theme.vars.newColor.white,
      border: `1px solid ${theme.vars.newColor.lightGS1}`,
      borderBottom: `${theme.vars.form.item.borderWidth} solid ${
        isFocused ? theme.vars.newColor.blue2 : theme.vars.newColor.lightGS1
      }`,
      color: theme.vars.newColor.darkGS1,
      display: 'flex',
      margin: '8px 12px',
      minHeight: '50px',
      outline: 'none',
      padding: '0 15px',
      position: 'relative',
      transition: theme.vars.transition.default,

      ':hover': {
        borderBottomColor: isFocused ? theme.vars.newColor.blue2 : theme.vars.newColor.lightGS1,
      },
    };
  },
  valueContainer: styles => ({
    ...styles,
    margin: 0,
    padding: 0,
  }),
  placeholder: styles => ({
    ...styles,
    color: theme.vars.newColor.lightGS1,
  }),
  menu: (styles, { selectProps: { isSearchable } }) => ({
    marginTop: isSearchable ? '8px' : null,
    width: '100%',
  }),
  option: (styles, { isFocused, isSelected }) => ({
    ...styles,
    backgroundColor: isSelected ? theme.vars.newColor.blue1 : isFocused ? theme.vars.newColor.blue4 : null,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',

    svg: {
      color: isSelected ? 'white' : null,
    },
  }),
});

const StyledSelect = styled(Select)``;

export interface ISelectMiniOption {
  value: string;
  label: string;
  icon?: ISelectIcon;
}

export type ISelectMiniMenuPlacement = 'right' | 'bottom' | 'bottomRight';

const createOptionFilter = (minLength: number, isSearchable: boolean) => {
  const defaultFilter = createFilter({
    ignoreAccents: false,
    stringify: option => option.label,
  });

  if (!isSearchable || minLength === 0) {
    return defaultFilter;
  }

  return (candidate, input) => {
    if (!input || input.length < minLength) {
      return false;
    }

    return defaultFilter(candidate, input);
  };
};

const createNoOptionsMessage = (
  minLength: number,
  isSearchable: boolean,
  searchMessage: string,
  defaultMessage: string,
) => {
  if (!isSearchable || minLength === 0) {
    return () => defaultMessage;
  }

  return input => (!input.inputValue || input.inputValue.length < minLength ? searchMessage : defaultMessage);
};

interface ISelectMiniProps {
  /** ARIA label tag */
  ariaLabel: string;
  /** inputId for the SelectMini component */
  inputId?: string;
  /** Available options  */
  options: ISelectMiniOption[];
  /** Selected value */
  value?: ISelectMiniOption;
  /** Default selected value */
  defaultValue?: ISelectMiniOption;
  /** Invoked on change select option */
  onChange?: (value: ISelectMiniOption) => void;
  /** Disables select element */
  disabled?: boolean;
  /** Width of select menu */
  menuWidth?: string;
  /** Placeholder when no options are selected */
  placeholder?: string;
  /** Placeholder which appears in the search box */
  searchPlaceholder?: string;
  /** Menu placement */
  menuPlacement?: ISelectMiniMenuPlacement;
  /** Whether you can search the options. Defaults to false. */
  searchable?: boolean;
  /** Minimum search term length before options are displayed. Defaults to 0. */
  minLength?: number;
  /** Placeholder which appears if more characters must be typed to search results */
  minLengthPlaceholder?: string;
  /** Placeholder which appears when there are no options to display */
  noOptionsPlaceholder?: string;
  /** Alphabetically sort the options by the label name.  */
  isSorted?: boolean;
  /** Show the dropdown arrow */
  showDropdownArrow?: boolean;
  /** Show radio button in menu */
  radioButtonMenu?: boolean;
  /** Show label for select mini */
  label?: string;
}

export const SelectMini: FC<ISelectMiniProps> = ({
  ariaLabel,
  inputId,
  options,
  value,
  defaultValue,
  onChange,
  disabled,
  menuWidth,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  minLengthPlaceholder = 'Type to search...',
  noOptionsPlaceholder = 'No options',
  minLength = 0,
  menuPlacement,
  searchable = false,
  isSorted,
  showDropdownArrow = true,
  radioButtonMenu,
  label,
}) => {
  const theme = useTheme();

  const [isOpen, setIsOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(value ? value : null);

  useEffect(() => {
    setSelectedItem(value ? value : null);
  }, [value]);

  const handleOnChange = (item: ISelectMiniOption) => {
    if (!value) {
      setSelectedItem(item ? item : null);
    }

    setIsOpen(false);

    onChange(item);
  };

  const handleOnClick = (e: React.MouseEvent) => {
    e.preventDefault();

    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!disabled && (e.keyCode === DOWN_KEY || e.keyCode === ENTER_KEY || e.keyCode === SPACE_KEY)) {
      e.preventDefault();
      setIsOpen(!isOpen);
    }
  };

  const handleBlanketClick = () => {
    setIsOpen(false);
  };

  const handleOnKeyDown = (e: React.KeyboardEvent) => {
    if (e.keyCode === ESCAPE_KEY) {
      setIsOpen(false);
    } else if (e.keyCode === TAB_KEY) {
      e.preventDefault();
    }
  };

  const formatOptionLabel = ({ icon, label }: ISelectMiniOption) => {
    if (!icon) {
      return label;
    }

    return (
      <>
        <IconWrapper>
          <Icon icon={icon.name} color={icon.color} />
        </IconWrapper>{' '}
        {label}
      </>
    );
  };

  let components: any = {
    DropdownIndicator: null,
    SingleValue: null,
    IndicatorSeperator: null,
  };

  if (options && options.length >= 100) {
    components = {
      ...components,
      MenuList,
    };
  }

  return (
    <SelectMiniContainer>
      {label && <Label style={{ marginRight: '8px' }} name={label} for={label} />}
      <SelectMiniDropdown>
        <SelectMiniButton
          aria-label={ariaLabel}
          data-testid="SelectMiniButton"
          disabled={disabled}
          onClick={handleOnClick}
          onKeyDownCapture={handleKeyDown}
        >
          <span data-testid="SelectMiniValue">{selectedItem ? selectedItem.label : placeholder}</span>

          {showDropdownArrow && (
            <SelectMiniDropdownIndicator>
              {isOpen ? (
                <SvgIcon name="angle-up" color={theme.vars.newColor.darkGS2} size="8" />
              ) : (
                <SvgIcon name="angle-down" color={theme.vars.newColor.darkGS2} size="8" />
              )}
            </SelectMiniDropdownIndicator>
          )}
        </SelectMiniButton>

        {!disabled && isOpen && (
          <SelectMiniMenu menuPlacement={menuPlacement} menuWidth={menuWidth} radioButtonMenu={radioButtonMenu}>
            {!radioButtonMenu ? (
              <>
                <StyledSelect
                  aria-label={ariaLabel}
                  inputId={inputId}
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus={true}
                  backspaceRemovesValue={false}
                  className="select-container"
                  classNamePrefix="select"
                  components={components}
                  controlShouldRenderValue={false}
                  defaultValue={defaultValue}
                  formatOptionLabel={formatOptionLabel}
                  isDisabled={disabled}
                  isSearchable={searchable}
                  filterOption={createOptionFilter(minLength, searchable)}
                  noOptionsMessage={createNoOptionsMessage(
                    minLength,
                    searchable,
                    minLengthPlaceholder,
                    noOptionsPlaceholder,
                  )}
                  menuIsOpen={true}
                  onChange={handleOnChange}
                  onKeyDown={handleOnKeyDown}
                  options={isSorted ? options.sort(sortOptions) : options}
                  placeholder={searchPlaceholder}
                  styles={SelectMiniStyles(theme)}
                  tabSelectsValue={false}
                  value={selectedItem}
                />

                <SelectMiniBlanket onClick={handleBlanketClick} />
              </>
            ) : (
              <RadioList compacted removeItemSpace={true}>
                {Object.entries(options).map(([key, item]) => {
                  return (
                    <RadioListItem
                      key={key}
                      heading={item.label}
                      id={`radio-list-item-${key}`}
                      onClick={() => handleOnChange(item)}
                      selected={selectedItem && selectedItem.label === item.label}
                      disabled={disabled}
                      radioItemSpace={'8px 16px'}
                    />
                  );
                })}
              </RadioList>
            )}
          </SelectMiniMenu>
        )}
      </SelectMiniDropdown>
    </SelectMiniContainer>
  );
};
